import { toast } from 'react-toastify';
import { AddCommentBody, Comment, CommentResponse, UpdateCommentParams } from '@core/models/comment.model';
import { IErrorModel, ISuccessResponse } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import CommentService from 'services/api/comment.service';
//----------------------------------------------------------------------------------------------------------
interface ICommentState {
    comments: Comment[];
    error: IErrorModel | undefined;
    loading: boolean;
    addLoading: boolean;
    updateLoading: boolean;
    deleteLoading: boolean;
}

const initialState: ICommentState = {
    comments: [],
    error: {} as IErrorModel,
    loading: false,
    addLoading: false,
    updateLoading: false,
    deleteLoading: false,
};
//----------------------------------------------------------------------------------------------------------
export const addCommentThunk = createAsyncThunk<ISuccessResponse, AddCommentBody, { rejectValue: IErrorModel }>(
    `/comment/addComment`,
    async (body, { rejectWithValue }: any) => {
        try {
            const response: any = await CommentService.addComment(body);
            toast.success(response.data.message || 'Comment added successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to add comment', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);
//----------------------------------------------------------------------------------------------------------
export const deleteCommentThunk = createAsyncThunk<ISuccessResponse, number, { rejectValue: IErrorModel }>(
    `/comment/deleteComment`,
    async (commentId, { rejectWithValue }: any) => {
        try {
            const response: any = await CommentService.deleteComment(commentId);
            toast.success(response.data.message || 'Comment deleted successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to delete comment', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);
//----------------------------------------------------------------------------------------------------------
export const updateCommentThunk = createAsyncThunk<ISuccessResponse, UpdateCommentParams, { rejectValue: IErrorModel }>(
    `/comment/updateComment`,
    async (params, { rejectWithValue }: any) => {
        try {
            const response: any = await CommentService.updateComment(params);
            toast.success(response.data.message || 'Comment updated successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to update comment', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);
//----------------------------------------------------------------------------------------------------------
export const getAllCommentsThunk = createAsyncThunk<Comment[], void, { rejectValue: IErrorModel }>(
    `/comment/getAllComments`,
    async (_, { rejectWithValue }: any) => {
        try {
            const response: any = await CommentService.getAllComments();
            return response.data.data || response.data || [];
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch comments', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);
//----------------------------------------------------------------------------------------------------------
const commentSlice = createSlice({
    name: `comment`,
    initialState,
    extraReducers: (builder) => {
        // ADD COMMENT
        builder.addCase(addCommentThunk.pending, (state) => {
            state.addLoading = true;
        });
        builder.addCase(addCommentThunk.fulfilled, (state) => {
            state.addLoading = false;
        });
        builder.addCase(addCommentThunk.rejected, (state, action) => {
            state.addLoading = false;
            state.error = action.payload;
        });
        // DELETE COMMENT
        builder.addCase(deleteCommentThunk.pending, (state) => {
            state.deleteLoading = true;
        });
        builder.addCase(deleteCommentThunk.fulfilled, (state) => {
            state.deleteLoading = false;
        });
        builder.addCase(deleteCommentThunk.rejected, (state, action) => {
            state.deleteLoading = false;
            state.error = action.payload;
        });
        // UPDATE COMMENT
        builder.addCase(updateCommentThunk.pending, (state) => {
            state.updateLoading = true;
        });
        builder.addCase(updateCommentThunk.fulfilled, (state) => {
            state.updateLoading = false;
        });
        builder.addCase(updateCommentThunk.rejected, (state, action) => {
            state.updateLoading = false;
            state.error = action.payload;
        });
        // GET ALL COMMENTS
        builder.addCase(getAllCommentsThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getAllCommentsThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.comments = action.payload;
        });
        builder.addCase(getAllCommentsThunk.rejected, (state, action) => {
            state.loading = false;
            state.comments = [];
            state.error = action.payload;
        });
    },
    reducers: {},
});
//----------------------------------------------------------------------------------------------------------
export default commentSlice;
export const commentReducer = commentSlice.reducer;
//----------------------------------------------------------------------------------------------------------

